// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';

// Storage keys
export const STORAGE_KEYS = {
    ACCESS_TOKEN: 'msk_user_access_token',
    REFRESH_TOKEN: 'msk_user_refresh_token',
    USER_DATA: 'msk_user_data'
};

// Create headers with authorization
const getHeaders = (includeAuth = true) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    if (includeAuth) {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }

    return headers;
};

// Handle API response
const handleResponse = async (response) => {
    const data = await response.json();
    // Keep API response payloads out of the browser console to protect user data.
    
    if (!response.ok) {
        // Check for account suspended (403) - ONLY check errorCode, not message content
        // لا نفحص محتوى الرسالة لأنها قد تحتوي على كلمات مشابهة بشكل غير مقصود
        if (response.status === 403 && data.errorCode === 'ACCOUNT_SUSPENDED') {
            // Update stored user data to mark as suspended
            try {
                const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
                if (userData) {
                    const user = JSON.parse(userData);
                    user.isActive = false;
                    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
                    // Dispatch custom event to notify AuthContext
                    window.dispatchEvent(new CustomEvent('userSuspended', { detail: user }));
                }
            } catch (e) {
                // Continue safely without logging stored user details.
            }
            return {
                success: false,
                message: 'حسابك معلق. يرجى التواصل مع الدعم الفني.',
                errorCode: 'ACCOUNT_SUSPENDED',
                data: null,
                statusCode: response.status
            };
        }
        
        // If token expired, try to refresh — only when backend explicitly says TOKEN_EXPIRED
        if (response.status === 401 && data.errorCode === 'TOKEN_EXPIRED') {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
                return null; // Signal to retry
            }
        }
        
        // For other errors, return the response data instead of throwing
        // This allows the caller to handle the error gracefully
        return {
            success: false,
            message: data.message || 'حدث خطأ في الاتصال',
            errorCode: data.errorCode || null,
            data: null,
            statusCode: response.status
        };
    }
    
    return data;
};

// Refresh access token — exported so AuthContext can call it on a timer
export const refreshAccessToken = async () => {
    try {
        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        if (!refreshToken) return false;

        const response = await fetch(`${API_BASE_URL}/user/refresh-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken })
        });

        if (!response.ok) {
            // Clear tokens and redirect to login
            clearTokens();
            window.location.href = '/login';
            return false;
        }

        const data = await response.json();
        if (data.success && data.data) {
            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.data.accessToken);
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.data.refreshToken);
            return true;
        }

        return false;
    } catch (error) {
        // Clear invalid credentials without exposing refresh details in the console.
        clearTokens();
        return false;
    }
};

// Clear all tokens
export const clearTokens = () => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
};

// API request wrapper with retry logic
const apiRequest = async (url, options = {}, retry = true) => {
    try {
        const response = await fetch(`${API_BASE_URL}${url}`, {
            ...options,
            headers: getHeaders(options.includeAuth !== false)
        });

        const data = await handleResponse(response);
        
        // If null, token was refreshed, retry the request
        if (data === null && retry) {
            return apiRequest(url, options, false);
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Multipart form data request (for file uploads)
const multipartRequest = async (url, formData, method = 'POST', retry = true) => {
    try {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        const headers = {};
        
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        // Note: Don't set Content-Type for multipart, browser will set it with boundary

        const response = await fetch(`${API_BASE_URL}${url}`, {
            method,
            headers,
            body: formData
        });

        const data = await handleResponse(response);
        
        // If null, token was refreshed, retry the request
        if (data === null && retry) {
            return multipartRequest(url, formData, method, false);
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Multipart request with XHR upload progress tracking
export const multipartRequestWithProgress = (method, url, formData, onProgress, token) => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, `${API_BASE_URL}${url}`);

        if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) {
                onProgress(Math.round((e.loaded / e.total) * 100));
            }
        };

        xhr.onload = () => {
            try {
                const data = JSON.parse(xhr.responseText);
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve(data);
                } else {
                    reject(new Error(data.message || 'حدث خطأ في الرفع'));
                }
            } catch {
                reject(new Error('فشل في قراءة الرد من الخادم'));
            }
        };

        xhr.onerror = () => reject(new Error('فشل الاتصال بالخادم'));
        xhr.send(formData);
    });
};

// API Methods
const api = {
    get: (url, includeAuth = true) => apiRequest(url, {
        method: 'GET',
        includeAuth
    }),
    post: (url, body, includeAuth = true) => apiRequest(url, { 
        method: 'POST', 
        body: JSON.stringify(body),
        includeAuth 
    }),
    put: (url, body) => apiRequest(url, { method: 'PUT', body: JSON.stringify(body) }),
    patch: (url, body) => apiRequest(url, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (url, body) => apiRequest(url, { method: 'DELETE', ...(body ? { body: JSON.stringify(body) } : {}) }),
    // For file uploads
    postMultipart: (url, formData) => multipartRequest(url, formData, 'POST'),
    putMultipart: (url, formData) => multipartRequest(url, formData, 'PUT')
};

export default api;
